import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import s from "./Equipment.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useAddNote, useEstateSummary, useInspections, useKpis, useNotes, useWorkOrders } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  defectFreeByType,
  inspectionRollup,
  lifeReport,
  moduleCsv,
  moduleWorkOrders,
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABEL,
  valueLabel,
  type EquipmentModule,
  type EstateSummary,
  type NoteCategory,
} from "../../domain/estate";
import { gradeKpi } from "../../domain/kpis";
import { isoDay, SLA_LABEL, slaState, workOrderStats } from "../../domain/workOrderReport";
import { isOpen, isOverdue, KIND_LABEL, STATUS_LABEL } from "../../domain/workOrders";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  numClass,
  Select,
  Skeleton,
  Table,
} from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { KpiTile } from "../dashboard/KpiTile";
import { downloadText } from "../work-orders/WorkOrdersPage";
import { WorkOrderStats } from "../work-orders/WorkOrderStats";
import { ConsumptionCard, TestComparisonCard } from "../testing/panels";
import { locale, t } from "../../i18n";

const pct = (n: number | null | undefined, digits = 1) =>
  n == null ? "—" : `${n.toFixed(digits).replace(/\.0$/, "")}%`;
const n0 = (n: number) => n.toLocaleString(locale());

/**
 * One class of airfield equipment, from the platform's own records: the
 * register, what is wrong, the work raised and its SLA, what is measured, the
 * rounds, and what engineers have written about it — on screen, as a printable
 * report, and as a CSV.
 */
export function EquipmentModulePage({ module }: { module: EquipmentModule }) {
  const { site, serverTime, principal, tenant } = useSession();
  const [params, setParams] = useSearchParams();
  const reporting = params.get("view") === "report";
  const requested = useMemo(() => [...module.assetTypes, ...(module.expectedTypes ?? []).map((x) => x.key)], [module]);
  const summary = useEstateSummary(requested);
  const orders = useWorkOrders({});
  const kpis = useKpis();
  const rounds = useInspections({});
  const now = useMemo(() => new Date(serverTime), [serverTime]);

  const mine = useMemo(() => moduleWorkOrders(orders.data?.items ?? [], module), [orders.data, module]);
  const woStats = useMemo(() => workOrderStats(mine, now), [mine, now]);
  const moduleKpis = useMemo(
    () => (kpis.data ?? []).filter((k) => module.kpis.includes(k.key)).map(gradeKpi),
    [kpis.data, module],
  );
  const inspections = useMemo(
    () => (module.inspectionTemplates.length ? inspectionRollup(rounds.data ?? [], module.inspectionTemplates) : null),
    [rounds.data, module],
  );

  const setReport = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set("view", "report");
    else next.delete("view");
    setParams(next, { replace: true });
  };

  return (
    <Page
      eyebrow={
        reporting ? t("{site} · airfield · report", { site: site.code }) : t("{site} · airfield", { site: site.code })
      }
      title={reporting ? t("{title} — report", { title: t(module.title) }) : module.title}
      actions={
        <div className={s.actions}>
          {reporting ? (
            <>
              <Button variant="primary" onClick={() => window.print()} disabled={!summary.data}>
                {t("Print / save as PDF")}
              </Button>
              <Button onClick={() => setReport(false)}>{t("Back to the module")}</Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => setReport(true)} disabled={!summary.data}>
              {t("Generate report")}
            </Button>
          )}
          <Button
            onClick={() =>
              summary.data &&
              downloadText(
                `${module.key}-${site.code}-${isoDay(now)}.csv`,
                moduleCsv(module, summary.data, mine),
                "text/csv;charset=utf-8",
              )
            }
            disabled={!summary.data}
          >
            {t("Export CSV")}
          </Button>
        </div>
      }
    >
      <div className={reporting ? `${s.module} ${s.report}` : s.module} data-testid={`module-${module.key}`}>
        {reporting ? (
          <header className={s.reportHead}>
            <div>{t(module.lede)}</div>
            <div className={s.meta}>
              {t("{site} · {name} · generated {date} by {person}", {
                site: site.code,
                name: site.name,
                date: formatDateTime(now.toISOString()),
                person: principal.name,
              })}
              {tenant ? ` · ${tenant}` : ""}
            </div>
          </header>
        ) : (
          <p className={s.lede}>{t(module.lede)}</p>
        )}

        {summary.isPending ? (
          <Skeleton rows={8} height={18} />
        ) : summary.isError ? (
          <ErrorState error={summary.error} retry={() => void summary.refetch()} />
        ) : (
          <Body
            module={module}
            s={summary.data}
            woStats={woStats}
            orders={mine}
            ordersLoaded={!!orders.data}
            kpis={moduleKpis}
            inspections={inspections}
            now={now}
            reporting={reporting}
          />
        )}

        <Notes module={module} reporting={reporting} canWrite={hasPermission(principal, PERMISSION.notesWrite)} />
      </div>
    </Page>
  );
}

function Body({
  module,
  s: sum,
  woStats,
  orders,
  ordersLoaded,
  kpis,
  inspections,
  now,
  reporting,
}: {
  module: EquipmentModule;
  s: EstateSummary;
  woStats: ReturnType<typeof workOrderStats>;
  orders: ReturnType<typeof moduleWorkOrders>;
  ordersLoaded: boolean;
  kpis: ReturnType<typeof gradeKpi>[];
  inspections: ReturnType<typeof inspectionRollup> | null;
  now: Date;
  reporting: boolean;
}) {
  const free = defectFreeByType(sum);
  const life = module.life ? lifeReport(sum, module.life) : null;
  const severe = sum.defects.openList.filter((d) => d.severity >= 4).length;
  const assetsWithReadings = new Set(sum.latestReadings.map((r) => r.assetTag)).size;
  const missing = (module.expectedTypes ?? []).filter((x) => sum.unknownAssetTypes.includes(x.key));
  const undeclared = module.assetTypes.filter((x) => sum.unknownAssetTypes.includes(x));
  const freeAll = sum.assetsTotal ? (100 * (sum.assetsTotal - sum.defects.assetsWithOpen)) / sum.assetsTotal : null;

  const tiles: { label: string; value: string; note?: string; bad?: boolean }[] = [
    {
      label: t("Assets registered"),
      value: n0(sum.assetsTotal),
      note: t("{n} asset types", { n: sum.assetTypes.filter((x) => x.assets).length }),
    },
    {
      label: t("Without an open defect"),
      value: pct(freeAll),
      note:
        sum.defects.assetsWithOpen === 1
          ? t("{n} asset carries one", { n: n0(sum.defects.assetsWithOpen) })
          : t("{n} assets carry one", { n: n0(sum.defects.assetsWithOpen) }),
    },
    {
      label: t("Open defects"),
      value: n0(sum.defects.open),
      note: t("{raised} raised · {resolved} resolved in 30 days", {
        raised: sum.defects.raised30d,
        resolved: sum.defects.resolved30d,
      }),
      bad: sum.defects.open > 0,
    },
    { label: t("Severity 4–5 open"), value: n0(severe), bad: severe > 0 },
    { label: t("Open alerts"), value: n0(sum.alerts.open), bad: sum.alerts.open > 0 },
    {
      label: t("Open work orders"),
      value: ordersLoaded ? n0(woStats.open) : "…",
      note: ordersLoaded
        ? t("{overdue} overdue · {breached} SLA breached", { overdue: woStats.overdue, breached: woStats.slaBreached })
        : undefined,
      bad: woStats.overdue > 0 || woStats.slaBreached > 0,
    },
    {
      label: t("Assets with readings"),
      value: n0(assetsWithReadings),
      note: sum.assetsTotal
        ? t("{pct} of the register", { pct: pct((100 * assetsWithReadings) / sum.assetsTotal) })
        : undefined,
    },
    {
      label: t("Condition scored"),
      value: n0(sum.assetTypes.reduce((a, x) => a + x.scored, 0)),
      note: sum.assetTypes.some((x) => x.scored)
        ? t("assets with a condition score")
        : t("no condition score recorded — not zero"),
    },
  ];
  if (inspections)
    tiles.push({
      label: t("Inspection rounds"),
      value: t("{n} done", { n: inspections.completed }),
      note: t("{n} scheduled · last {date}", {
        n: inspections.scheduled,
        date: inspections.lastCompletedAt ? formatDate(inspections.lastCompletedAt) : t("never"),
      }),
    });
  if (life)
    tiles.push({
      label: t("{label} ≥ 80%", { label: t(module.life!.label) }),
      value: n0(life.over80),
      note: t("{over} past rated · of {judged} with a rating", { over: life.over100, judged: life.judged }),
      bad: life.over100 > 0,
    });

  return (
    <>
      {(missing.length > 0 || undeclared.length > 0) && (
        <div className={s.notice} role="status">
          {undeclared.length > 0 && (
            <div>
              {t("Not declared by the installed packs: {types}. Nothing of that type can be shown.", {
                types: undeclared.join(", "),
              })}
            </div>
          )}
          {missing.length > 0 && (
            <div>
              {t(
                "Not in the register: {types}. This doesn't mean none are installed.",
                { types: missing.map((m) => t(m.label)).join(", ") },
              )}
            </div>
          )}
        </div>
      )}

      <div className={s.tiles}>
        {tiles.map((tile) => (
          <div key={tile.label} className={s.tile}>
            <div className={s.tileLabel}>{tile.label}</div>
            <div className={tile.bad ? `${s.tileValue} ${s.bad}` : s.tileValue}>{tile.value}</div>
            {tile.note && <div className={s.tileNote}>{tile.note}</div>}
          </div>
        ))}
      </div>

      {kpis.length > 0 && (
        <Card title="Platform KPIs" sub="Computed and graded by the platform">
          <div className={s.kpis}>
            {kpis.map((k) => (
              <KpiTile key={k.key} kpi={k} />
            ))}
          </div>
        </Card>
      )}

      {(module.tests ?? []).map((mt) => (
        <TestComparisonCard
          key={mt.testType}
          testType={mt.testType}
          title={mt.title}
          unit={t(mt.unit)}
          groupBy={mt.groupBy}
          reporting={reporting}
        />
      ))}

      {module.consumption && <ConsumptionCard assetTypes={module.assetTypes} reporting={reporting} />}

      <Card title="Register" sub="Recorded assets by type, and gaps">
        {sum.assetTypes.length === 0 ? (
          <EmptyState>No asset of these types is registered at this site.</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("Asset type")}</th>
                  <th style={{ textAlign: "end" }}>{t("Assets")}</th>
                  <th style={{ textAlign: "end" }}>{t("Located")}</th>
                  <th style={{ textAlign: "end" }}>{t("With an open defect")}</th>
                  <th style={{ textAlign: "end" }}>{t("Without one")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {sum.assetTypes.map((at) => (
                  <tr key={at.key}>
                    <td>{at.name}</td>
                    <td className={numClass}>{n0(at.assets)}</td>
                    <td className={numClass}>{n0(at.located)}</td>
                    <td className={numClass}>{free[at.key]?.withOpen ?? 0}</td>
                    <td className={numClass}>{pct(free[at.key]?.pct)}</td>
                    <td>
                      {Object.entries(at.byStatus)
                        .map(([k, v]) => `${valueLabel(k)} ${v}`)
                        .join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className={s.bars}>
              {sum.assetTypes.flatMap((at) =>
                Object.entries(module.attributes[at.key] ?? {})
                  .filter(([attr]) => at.attributes[attr])
                  .map(([attr, label]) => {
                    const b = at.attributes[attr];
                    const max = Math.max(1, ...b.values.map((v) => v.count), b.notRecorded);
                    return (
                      <section
                        key={`${at.key}.${attr}`}
                        className={s.barCard}
                        aria-label={t("{type} by {attribute}", { type: at.name, attribute: t(label) })}
                      >
                        <h3 className={s.barTitle}>
                          {at.name} · {t(label)}
                        </h3>
                        <ul className={s.barList}>
                          {b.values.slice(0, 12).map((v) => (
                            <Bar
                              key={v.value}
                              label={valueLabel(v.value)}
                              count={v.count}
                              max={max}
                              total={at.assets}
                            />
                          ))}
                          {b.values.length > 12 && (
                            <li className={s.tileNote}>{t("… {n} more values", { n: b.values.length - 12 })}</li>
                          )}
                          {b.notRecorded > 0 && (
                            <Bar label={t("Not recorded")} count={b.notRecorded} max={max} total={at.assets} muted />
                          )}
                        </ul>
                      </section>
                    );
                  }),
              )}
            </div>
          </>
        )}
      </Card>

      <Card
        title="Open defects"
        sub={t("{n} open on {assets} assets", { n: sum.defects.open, assets: sum.defects.assetsWithOpen })}
      >
        {sum.defects.open === 0 ? (
          <EmptyState>No open defect on this equipment.</EmptyState>
        ) : (
          <>
            <div className={s.chips}>
              {sum.defects.byTypeSeverity
                .sort((a, b) => b.severity - a.severity || b.count - a.count)
                .map((x) => (
                  <span key={`${x.defectType}.${x.severity}`} className={s.chip}>
                    <b>{x.count}</b> {x.defectType} · S{x.severity}
                  </span>
                ))}
            </div>
            <Table>
              <thead>
                <tr>
                  <th>{t("Sev")}</th>
                  <th>{t("Asset")}</th>
                  <th>{t("Defect")}</th>
                  <th>{t("Summary")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Detected")}</th>
                  <th style={{ textAlign: "end" }}>{t("Age")}</th>
                </tr>
              </thead>
              <tbody>
                {sum.defects.openList.map((d) => (
                  <tr key={d.id}>
                    <td className={d.severity >= 4 ? `${numClass} ${s.bad}` : numClass}>S{d.severity}</td>
                    <td className="mono">{d.assetTag}</td>
                    <td>{d.defectTypeName ?? "—"}</td>
                    <td>{d.summary}</td>
                    <td>{valueLabel(d.status)}</td>
                    <td>{formatDate(d.detectedAt)}</td>
                    <td className={numClass}>
                      {t("{n}d", {
                        n: Math.max(0, Math.floor((now.getTime() - Date.parse(d.detectedAt)) / 86_400_000)),
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {sum.defects.openListTotal > sum.defects.openList.length && (
              <div className={s.tileNote}>
                {t("… and {n} more not listed.", { n: sum.defects.openListTotal - sum.defects.openList.length })}
              </div>
            )}
          </>
        )}
      </Card>

      <Card
        title="Work orders"
        sub="Raised for this equipment"
        actions={
          !reporting && (
            <Link
              to={`/work-orders?scope=all&${new URLSearchParams(
                sum.assetTypes
                  .filter((x) => x.assets)
                  .slice(0, 1)
                  .map((x) => ["repair", x.name]),
              )}`}
            >
              {t("Open in Work orders →")}
            </Link>
          )
        }
      >
        {!ordersLoaded ? (
          <Skeleton rows={3} />
        ) : orders.length === 0 ? (
          <EmptyState>No work order has been raised against this equipment.</EmptyState>
        ) : (
          <>
            <WorkOrderStats stats={woStats} interactive={false} />
            <Table>
              <thead>
                <tr>
                  <th>{t("Number")}</th>
                  <th>{t("Work")}</th>
                  <th>{t("Kind")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("SLA")}</th>
                  <th>{t("Opened")}</th>
                </tr>
              </thead>
              <tbody>
                {orders
                  .slice()
                  .sort((a, b) => Number(isOpen(b.status)) - Number(isOpen(a.status)) || a.priority - b.priority)
                  .map((w) => (
                    <tr key={w.id}>
                      <td className="mono">
                        {reporting ? w.number : <Link to={`/work-orders/${w.id}`}>{w.number}</Link>}
                      </td>
                      <td>
                        {w.title}
                        <div className={s.tileNote}>
                          P{w.priority} · {w.assetTag ?? "—"}
                          {isOverdue(w, now) ? ` · ${t("OVERDUE")}` : ""}
                        </div>
                      </td>
                      <td>{t(KIND_LABEL[w.kind])}</td>
                      <td>{t(STATUS_LABEL[w.status])}</td>
                      <td>{w.slaHours == null ? "—" : `${t(SLA_LABEL[slaState(w, now)])} · ${w.slaHours} h`}</td>
                      <td>{formatDate(w.openedAt)}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </>
        )}
      </Card>

      <Card title="Readings" sub="what is measured on this equipment, and how recently">
        {sum.readings.length === 0 ? (
          <EmptyState>No reading is recorded against this equipment.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Measurement")}</th>
                <th>{t("Asset type")}</th>
                <th style={{ textAlign: "end" }}>{t("Readings")}</th>
                <th style={{ textAlign: "end" }}>{t("Assets")}</th>
                <th>{t("First")}</th>
                <th>{t("Latest")}</th>
              </tr>
            </thead>
            <tbody>
              {sum.readings.map((r) => (
                <tr key={`${r.assetType}.${r.measurement}`}>
                  <td>
                    {r.measurementName}
                    {r.unit ? <span className="muted"> ({r.unit})</span> : null}
                  </td>
                  <td>{sum.assetTypes.find((x) => x.key === r.assetType)?.name ?? r.assetType}</td>
                  <td className={numClass}>{n0(r.readings)}</td>
                  <td className={numClass}>{n0(r.assets)}</td>
                  <td>{formatDate(r.firstAt)}</td>
                  <td>{formatDate(r.latestAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {life && module.life && (
        <Card
          title={module.life.label}
          sub={t(
            "Hours run vs. rated hours: {judged} judged, {unrated} without a rating",
            { judged: life.judged, unrated: life.unrated },
          )}
        >
          {life.rows.length === 0 ? (
            <EmptyState>No asset has both hours run and a rating yet.</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Asset")}</th>
                  <th style={{ textAlign: "end" }}>{t("Hours run")}</th>
                  <th style={{ textAlign: "end" }}>{t("Rated")}</th>
                  <th>{t("Life used")}</th>
                  <th>{t("Read")}</th>
                </tr>
              </thead>
              <tbody>
                {life.rows.slice(0, reporting ? 50 : 15).map((r) => (
                  <tr key={r.assetTag}>
                    <td className="mono">{r.assetTag}</td>
                    <td className={numClass}>{n0(Math.round(r.hours))}</td>
                    <td className={numClass}>{n0(r.rated)}</td>
                    <td>
                      <div className={s.lifeBar} title={pct(r.pct)}>
                        <span
                          style={{ width: `${Math.min(100, r.pct)}%` }}
                          className={r.pct >= 100 ? s.lifeOver : r.pct >= 80 ? s.lifeHigh : undefined}
                        />
                      </div>
                      <span className={r.pct >= 100 ? s.bad : undefined}>{pct(r.pct, 0)}</span>
                    </td>
                    <td>{formatDate(r.observedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {sum.alerts.open > 0 && (
        <Card title="Open alerts" sub="what the rules have raised on this equipment">
          <Table>
            <thead>
              <tr>
                <th>{t("Sev")}</th>
                <th>{t("Alert")}</th>
                <th>{t("Asset")}</th>
                <th>{t("Kind")}</th>
                <th>{t("Raised")}</th>
              </tr>
            </thead>
            <tbody>
              {sum.alerts.list.slice(0, reporting ? 100 : 20).map((a) => (
                <tr key={a.id}>
                  <td className={a.severity >= 4 ? `${numClass} ${s.bad}` : numClass}>{a.severity}</td>
                  <td>{reporting ? a.title : <Link to={`/alerts?open=${a.id}`}>{a.title}</Link>}</td>
                  <td className="mono">{a.assetTag}</td>
                  <td>{valueLabel(a.kind)}</td>
                  <td>{formatDate(a.raisedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

function Bar({
  label,
  count,
  max,
  total,
  muted,
}: {
  label: string;
  count: number;
  max: number;
  total: number;
  muted?: boolean;
}) {
  return (
    <li className={s.bar} title={t("{label}: {count} of {total}", { label, count, total })}>
      <span className={muted ? `${s.barLabel} muted` : s.barLabel}>{label}</span>
      <span className={s.barTrack}>
        <span
          className={muted ? `${s.barFill} ${s.barMuted}` : s.barFill}
          style={{ width: `${(100 * count) / max}%` }}
        />
      </span>
      <span className={s.barCount}>{n0(count)}</span>
    </li>
  );
}

/** Engineers' notes on this module: permanent once written, with who and when. */
function Notes({ module, reporting, canWrite }: { module: EquipmentModule; reporting: boolean; canWrite: boolean }) {
  const notes = useNotes(module.notesTopic);
  const add = useAddNote(module.notesTopic);
  const [category, setCategory] = useState<NoteCategory>("observation");
  const [assetTag, setAssetTag] = useState("");
  const [body, setBody] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    add.mutate(
      { category, body: body.trim(), assetTag: assetTag.trim() || undefined },
      {
        onSuccess: () => {
          setBody("");
          setAssetTag("");
        },
      },
    );
  };

  return (
    <Card
      title="Engineer notes"
      sub="Notes can't be edited. Add a new note to correct one."
    >
      {!reporting &&
        (canWrite ? (
          <form className={s.noteForm} onSubmit={submit} aria-label={t("Add an engineer note")}>
            <div className={s.noteRow}>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as NoteCategory)}
                aria-label={t("Note category")}
              >
                {NOTE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(NOTE_CATEGORY_LABEL[c])}
                  </option>
                ))}
              </Select>
              <Input
                value={assetTag}
                onChange={(e) => setAssetTag(e.target.value)}
                placeholder={t("Asset tag (optional)")}
                aria-label={t("Asset tag")}
                style={{ width: 200 }}
              />
            </div>
            <textarea
              className={s.noteBody}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder={t("e.g. Circuit C4 trips after heavy rain — megger the feeder before the next night round.")}
              aria-label={t("Note")}
            />
            <div className={s.noteRow}>
              {add.isError && (
                <span className={s.bad} role="alert">
                  {t("Not saved: {message}", { message: (add.error as Error).message })}
                </span>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={!body.trim() || add.isPending}
                style={{ marginInlineStart: "auto" }}
              >
                {add.isPending ? t("Saving…") : t("Add note")}
              </Button>
            </div>
          </form>
        ) : (
          <div className={s.tileNote}>
            {t("Reading only — writing a note needs {permission}.", { permission: PERMISSION.notesWrite })}
          </div>
        ))}
      {notes.isPending ? (
        <Skeleton rows={2} />
      ) : notes.isError ? (
        <ErrorState error={notes.error} retry={() => void notes.refetch()} />
      ) : notes.data.length === 0 ? (
        <EmptyState>No engineer has written a note on this equipment yet.</EmptyState>
      ) : (
        <ol className={s.notes}>
          {notes.data.map((n) => (
            <li key={n.id} className={s.note} data-category={n.category}>
              <div className={s.noteHead}>
                <span className={s.noteCat}>{t(NOTE_CATEGORY_LABEL[n.category])}</span>
                {n.assetTag && <span className="mono">{n.assetTag}</span>}
                <span className={s.noteMeta}>
                  {n.createdBy ?? t("unrecorded")} · {formatDateTime(n.createdAt)}
                </span>
              </div>
              <div className={s.noteText}>{n.body}</div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
